import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui';
import { api } from '../lib/api';
import { openSignedInTap } from '../utils/openDocument';
import FileItDialog, { type FileItDocument } from '../components/documents/FileItDialog';
import LibraryFileRow, { type Filing, type LibraryFile } from '../components/documents/LibraryFileRow';
import {
  Breadcrumb, FolderTiles, SubfolderList, type Crumb, type FoldersData,
} from '../components/documents/FolderView';
import { KINDS, kindFolderLabel, placeLabel, type LibraryPlace } from '../components/documents/libraryKinds';

/**
 * DOC1 - the main folder. His words, 2026-09-04: *"shouldn't there be like a documents main folder
 * like google drive... and then the documents show up at the right deal or property."*
 *
 * ⭐⭐⭐ MEASURED LIVE BEFORE BUILDING (2026-09-05): 99 files, 99 links, and **15 files filed
 * NOWHERE** - Costar profiles, title reports, an OM, Riley's options explainer, five AIR contract
 * revisions, two LOI `.docx`s and a brochure. Every other surface in the app queries `file_links`
 * by target, so **no page could show one of them.** That is DOC0's defect one level up, and those
 * 15 are the most useful thing this page displays.
 *
 * ⭐⭐ ONE ROW PER DOCUMENT, NOT PER FILING. `files` is deduped per org on sha256, which is what
 * makes it a folder rather than a list of placements; 9 documents are filed in two places and a
 * filing-keyed list would print each of them twice while still hiding all 15.
 *
 * FV (2026-09-08) - THE FOLDER VIEW. His words: *"like a mac almost - folders like properties or
 * listings or proposals, just a simple way to organize stuff."* Folders are the default and the
 * list is one click away; both render the same rows. Measured before designing: 100 files,
 * 39 properties holding documents (30 of them exactly one), 2 deals, 1 portal, 16 filed nowhere -
 * a tree two levels deep, not a taxonomy. ⭐ The folder path lives in the URL so a refresh and
 * the back button keep the place.
 *
 * ⛔ THE NAV'S "Documents" USED TO OPEN THE GENERATOR. `docs/BROWSER-VERIFICATION.md` already
 * records that trap: *"The Documents page offered a wrong A4-portrait generator where he actually
 * looked."* It now opens this; the generator kept its own entry, named for what it does.
 */

const PAGE = 50;

const FILTERS: Array<{ key: string; label: string; targetType?: string; needsAHome?: boolean }> = [
  { key: 'all', label: 'All' },
  { key: 'needs', label: 'Needs a home', needsAHome: true },
  { key: 'property', label: 'On a property', targetType: 'property' },
  { key: 'master_property', label: 'On a database record', targetType: 'master_property' },
  { key: 'deal', label: 'On a deal', targetType: 'deal' },
  { key: 'portal', label: 'In a client portal', targetType: 'portal' },
];

const PLACE_KEYS: readonly string[] = ['property', 'master_property', 'deal', 'portal', 'contact', 'company'];

const chip = (on: boolean): string =>
  `rounded border px-2.5 py-1 text-[11.5px] font-semibold ${
    on ? 'border-primary-500 bg-primary-50 text-primary-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
  }`;

export default function DocumentLibrary() {
  // ---- where we are: the folder path is the URL ------------------------------------------
  const [params, setParams] = useSearchParams();
  const view: 'folders' | 'list' = params.get('view') === 'list' ? 'list' : 'folders';
  const placeParam = params.get('place');
  const place: LibraryPlace | null = placeParam && PLACE_KEYS.includes(placeParam) ? (placeParam as LibraryPlace) : null;
  const folderId = params.get('id');
  const kindParam = params.get('kind');
  const needsParam = params.get('needs') === '1';

  /** Replace the folder path wholesale - every key not named is dropped, so a path never leaks. */
  const go = useCallback((next: Record<string, string | null>) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(next)) if (v) p.set(k, v);
    setParams(p);
  }, [setParams]);

  const [files, setFiles] = useState<LibraryFile[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  // The LIST view's own chips (DOC1, DOC1c). Independent of the folder path.
  const [filter, setFilter] = useState('all');
  /** DOC1c: independent of `filter` - WHERE a document is filed and WHAT it is are two questions. */
  const [kind, setKind] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  // DOC1b. `filing` is the document whose dialog is open; `removing` is the link being taken off.
  const [filing, setFiling] = useState<FileItDocument[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [removing, setRemoving] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');
  const [opening, setOpening] = useState<string | null>(null);
  /** Why ONE row's document did not open - said on that row (see `LibraryFileRow`'s `openError`). */
  const [openError, setOpenError] = useState<{ fileId: string; message: string } | null>(null);

  // FV: the map of the library - places, folders, kinds, each with its count.
  const [folders, setFolders] = useState<FoldersData | null>(null);
  const [foldersError, setFoldersError] = useState('');

  // A keystroke is not a query. Debounced so typing a filename is one read, not eleven.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const active = useMemo(() => FILTERS.find((f) => f.key === filter) ?? FILTERS[0], [filter]);

  /**
   * Which of the three levels is on screen.
   *
   * ⭐ A search inside a folder searches THAT folder; a search from the top searches everything.
   * That is what a Mac does, and it keeps "what am I looking at" a question the breadcrumb answers.
   */
  const inFolder = !!(folderId || kindParam || needsParam);
  const level: 'tiles' | 'subfolders' | 'rows' =
    view === 'list' || debounced || inFolder ? 'rows' : place ? 'subfolders' : 'tiles';

  /**
   * E1 retention. ⭐ Computed on request by the server, so it is always current and there is no cron.
   * ⛔ It lives HERE and not on the sidebar's Needs Attention count, which reads `lead_flags` and points
   *   at a CRM page - `navCountsController`'s rule is that a count beside a link is a promise about the
   *   page behind it, and a document warning on a CRM count would make the sidebar disagree with itself.
   */
  const [retention, setRetention] = useState<{ warning: string | null; ageing: number; due: number } | null>(null);

  useEffect(() => {
    let live = true;
    api.libraryRetention()
      .then((r) => { if (live) setRetention(r.data); })
      /** ⭐ A warning that cannot be read must never take the Documents page down with it. */
      .catch(() => { if (live) setRetention(null); });
    return () => { live = false; };
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.listLibraryFiles(
        view === 'list'
          ? {
              search: debounced || undefined,
              targetType: active.targetType,
              documentType: kind ?? undefined,
              needsAHome: active.needsAHome,
              limit: PAGE,
              offset,
            }
          : {
              search: debounced || undefined,
              // FV: one record's folder - the id alone, whichever surface filed the document.
              targetId: folderId ?? undefined,
              documentType: kindParam ?? undefined,
              needsAHome: needsParam,
              limit: PAGE,
              offset,
            }
      );
      setFiles((r.files ?? []) as LibraryFile[]);
      setTotal(r.total ?? 0);
      setError('');
    } catch (e) {
      // ⛔ A FAILURE IS NOT AN EMPTY SHELF. `files` stays null so "no documents" cannot render.
      setError(e instanceof Error ? e.message : 'Could not load the documents');
    }
  }, [view, debounced, active, kind, offset, folderId, kindParam, needsParam]);

  const loadFolders = useCallback(async () => {
    try {
      const r = await api.listLibraryFolders();
      setFolders({ total: r.total, needsAHome: r.needsAHome, places: r.places, kinds: r.kinds });
      setFoldersError('');
    } catch (e) {
      // ⛔ Same rule as the rows: a failure must not render as an empty desk.
      setFolders(null);
      setFoldersError(e instanceof Error ? e.message : 'Could not load the folders');
    }
  }, []);

  useEffect(() => { if (level === 'rows') void load(); }, [level, load]);
  useEffect(() => { void loadFolders(); }, [loadFolders]);
  useEffect(() => { setOffset(0); }, [debounced, filter, kind, view, folderId, kindParam, needsParam]);
  /**
   * ⛔ THE SELECTION CLEARS WHEN THE VISIBLE SET CHANGES. A row ticked under "Needs a home" and
   * then filtered away is a document he can no longer see, and filing what you cannot see is
   * exactly the silent guess this programme refuses.
   */
  useEffect(() => { setSelected(new Set()); }, [debounced, filter, kind, offset, view, folderId, kindParam, needsParam]);

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /** Anything that moves a filing also moves the folder counts. */
  const refresh = async () => { await Promise.all([load(), loadFolders()]); };

  /**
   * DOC1b. Take a filing back off - the correction path, and the reason "File it" is safe to ship.
   *
   * ⭐⭐ THE DOCUMENT AND ITS BYTES STAY. This drops the `file_links` row only; Joseph's standing
   * rule for client documents is UNLINK, NEVER HARD-DELETE, because there is no PITR on this
   * project and the retained storage object IS the rollback (B37). A misfile costs a click.
   */
  const unfile = async (f: LibraryFile, x: Filing) => {
    setRemoving(x.linkId);
    setActionError('');
    try {
      await api.unfileDocument(f.id, x.linkId);
      await refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not remove that filing');
    } finally {
      setRemoving(null);
    }
  };

  const open = async (f: LibraryFile) => {
    setOpening(f.id);
    setOpenError(null);
    try {
      // MOB4: the window opens INSIDE the tap and is pointed at the link when it is signed. Signing
      // first and opening after is a dead button on an iPhone at its default setting (measured).
      // ⚠️ The in-app Browser pane refuses every new window (runbook trap 12) - this works in his Chrome.
      await openSignedInTap(() => api.downloadLibraryFile(f.id).then((r) => r.url));
    } catch (e) {
      // ⛔ NOT `error` (found by reading, the 130th): `error` is the LOAD's, and setting it replaced the whole list
      // with "Could not load the documents - Could not open the document". An open that failed is not a load that
      // failed - the reason is said ON THE ROW and the list stays.
      setOpenError({ fileId: f.id, message: e instanceof Error ? e.message : 'Could not open the document' });
    } finally {
      setOpening(null);
    }
  };

  /**
   * LOI-PDF1b (the 130th, his yes). A letter as a real PDF made from its Word file - header, footer and all. The
   * button is `OpenPdfButton` (the window inside the tap, the wait, the closed window on a failure).
   *
   * ⛔ HE IS NEVER LEFT WITH NOTHING. A PDF that is refused (a Word file edited since, no converter on this server) or
   *    fails: this says why, in the server's words, and names the button that still works. The library has no viewer
   *    of its own and the tap is spent - so it points, it does not open.
   */
  const pdfFailed = (f: LibraryFile, message: string) =>
    setOpenError({ fileId: f.id, message: `${message} The Word file is still here - use "Word file".` });

  const toFileIt = (f: LibraryFile): FileItDocument => ({
    id: f.id, filename: f.filename, filingHint: f.filingHint, filingHintKey: f.filingHintKey,
  });

  // ---- the breadcrumb: where we are, and the way back ------------------------------------
  const crumbs: Crumb[] = [{ label: 'Documents', onClick: () => go({}) }];
  if (view === 'list') crumbs.push({ label: 'All documents' });
  else {
    if (place) {
      crumbs.push({ label: placeLabel(place), onClick: folderId ? () => go({ place }) : undefined });
      if (folderId) {
        const found = folders?.places.find((p) => p.place === place)?.folders.find((f) => f.targetId === folderId);
        crumbs.push({ label: found?.label ?? '...' });
      }
    }
    if (kindParam) crumbs.push({ label: kindFolderLabel(kindParam) });
    if (needsParam) crumbs.push({ label: 'Needs a home' });
    if (debounced) crumbs.push({ label: `Search: "${debounced}"` });
  }

  return (
    <Layout>
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">
              Documents
            </h1>
            <p className="mt-1 text-[13px] text-gray-500">
              Every document Apex holds, and where each one is filed. A document filed in two places
              is one row here.
            </p>
          </div>
          {/* FV: two ways of looking at one library. The list keeps its chips; the folders keep
              their path. Both are doors, so both say what they open. */}
          <div className="flex gap-1.5" role="group" aria-label="View">
            <button type="button" onClick={() => go({})} aria-pressed={view === 'folders'} className={chip(view === 'folders')}>
              Folders
            </button>
            <button type="button" onClick={() => go({ view: 'list' })} aria-pressed={view === 'list'} className={chip(view === 'list')}>
              List
            </button>
          </div>
        </div>

        <Card>
          <CardContent className="pt-5">
            <div className="flex flex-wrap items-center gap-2.5">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={inFolder ? 'Search this folder by file name' : 'Search by file name'}
                aria-label="Search documents"
                className="min-w-0 flex-1 basis-[220px] rounded border border-gray-300 px-3 py-1.5 text-[13px] text-gray-800 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none"
              />
              {view === 'list' && (
                <div className="flex flex-wrap gap-1.5">
                  {FILTERS.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setFilter(f.key)}
                      aria-pressed={filter === f.key}
                      className={chip(filter === f.key)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {view === 'list' && (
              /* DOC1c. A SECOND ROW, deliberately: WHERE a document is filed and WHAT it is are two
                 questions, and folding them into one row would make them look mutually exclusive. */
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="text-[11.5px] text-gray-400">Kind</span>
                {KINDS.map((k) => (
                  <button
                    key={k.key}
                    type="button"
                    onClick={() => setKind((v) => (v === k.key ? null : k.key))}
                    aria-pressed={kind === k.key}
                    className={chip(kind === k.key)}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
            )}

            {view === 'folders' && (
              <div className="mt-3">
                <Breadcrumb crumbs={crumbs} />
              </div>
            )}

            {/*
              E1 retention's warning. ⛔ IT NEVER SAYS "DELETED", because nothing is: the documents stay
              in the library and only leave the review queue. A person reading "12 documents will be
              deleted" about a client's paperwork acts on a fear that is not true here.
              ⭐ It names its contents and offers the way to act - his standing rule that a control must
              say what is behind it, learned when "Show all 7" read to him as the documents having gone.
            */}
            {retention?.warning && (
              <div
                data-testid="retention-warning"
                className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
              >
                <span aria-hidden className="mt-0.5 text-amber-600">⚠</span>
                <div className="flex-1 text-sm text-amber-900">
                  <p>{retention.warning}</p>
                  <button
                    type="button"
                    onClick={() => go({ needs: '1' })}
                    className="mt-1 font-medium underline underline-offset-2 hover:text-amber-950"
                  >
                    Show the {retention.ageing + retention.due} unfiled document
                    {retention.ageing + retention.due === 1 ? '' : 's'}
                  </button>
                </div>
              </div>
            )}

            {/* ---- level 0 and 1: the folders ---------------------------------------------- */}
            {level !== 'rows' && foldersError && (
              <p className="mt-4 text-[12.5px] text-gray-500">Could not load the folders - {foldersError}</p>
            )}
            {level === 'tiles' && folders && (
              <div className="mt-4">
                <FolderTiles
                  data={folders}
                  onPlace={(p) => go({ place: p })}
                  onKind={(k) => go({ kind: k })}
                  onNeedsAHome={() => go({ needs: '1' })}
                />
              </div>
            )}
            {level === 'subfolders' && folders && place && (
              <div className="mt-4">
                <SubfolderList
                  place={place}
                  folders={folders.places.find((p) => p.place === place)?.folders ?? []}
                  onOpen={(f) => go({ place, id: f.targetId })}
                />
              </div>
            )}

            {/* ---- level 2 and the list: the rows ------------------------------------------ */}
            {level === 'rows' && error && (
              <p className="mt-4 text-[12.5px] text-gray-500">Could not load the documents - {error}</p>
            )}

            {level === 'rows' && !error && files && (
              <>
                {/* ⭐ THE COUNT IS THE TOTAL THAT MATCHED, not the length of this page. Deriving it
                    from the rendered rows would make the number a lie the moment paging starts. */}
                <p className="mt-3 text-[11.5px] text-gray-400">
                  {total === 0
                    ? 'No documents'
                    : `${total} document${total === 1 ? '' : 's'}${
                        files.length < total ? ` · showing ${offset + 1}-${offset + files.length}` : ''
                      }`}
                </p>

                {total === 0 && (
                  <p className="mt-2.5 text-[12.5px] text-gray-500">
                    No documents match. Anything uploaded to a property, a deal or a portal shows up here.
                  </p>
                )}

                {selected.size > 0 && (
                  /* DOC1d. ⭐⭐⭐ Measured: EIGHT of the fifteen unfiled documents are one building,
                     so the tedium this removes is real - and one wrong pick would misfile all
                     eight, which is why the dialog names every file before it writes. */
                  <div
                    data-selection-bar
                    className="mt-2 flex flex-wrap items-center gap-2 rounded border border-gray-300 bg-gray-50 px-2.5 py-1.5"
                  >
                    <span className="text-[12px] font-semibold text-gray-700">
                      {selected.size} selected
                    </span>
                    <button
                      type="button"
                      onClick={() => setFiling((files ?? []).filter((f) => selected.has(f.id)).map(toFileIt))}
                      className="rounded bg-gray-900 px-2.5 py-1 text-[11.5px] font-semibold text-white"
                    >
                      File {selected.size === 1 ? 'it' : 'them'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelected(new Set())}
                      className="rounded border border-gray-300 px-2.5 py-1 text-[11.5px] font-semibold text-gray-700 hover:bg-white"
                    >
                      Clear
                    </button>
                  </div>
                )}

                <ul className="mt-2 divide-y divide-gray-150">
                  {files.map((f) => (
                    <LibraryFileRow
                      key={f.id}
                      file={f}
                      selected={selected.has(f.id)}
                      onToggle={() => toggle(f.id)}
                      onFileIt={() => setFiling([toFileIt(f)])}
                      onOpen={() => void open(f)}
                      opening={opening === f.id}
                      signPdf={() => api.libraryFilePdf(f.id).then((r) => r.url)}
                      onPdfStart={() => setOpenError(null)}
                      onPdfFailed={(message) => pdfFailed(f, message)}
                      openError={openError?.fileId === f.id ? openError.message : null}
                      onUnfile={(x) => void unfile(f, x)}
                      removing={removing}
                    />
                  ))}
                </ul>

                {total > PAGE && (
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      disabled={offset === 0}
                      onClick={() => setOffset(Math.max(0, offset - PAGE))}
                      className="rounded border border-gray-300 px-2.5 py-1 text-[11.5px] font-semibold text-gray-700 disabled:opacity-40"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      disabled={offset + PAGE >= total}
                      onClick={() => setOffset(offset + PAGE)}
                      className="rounded border border-gray-300 px-2.5 py-1 text-[11.5px] font-semibold text-gray-700 disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {actionError && (
          <p className="mt-2 rounded border border-red-300 bg-red-50 px-2.5 py-2 text-[12px] text-red-700">
            {actionError}
          </p>
        )}

        <FileItDialog
          open={filing.length > 0}
          documents={filing}
          onClose={() => setFiling([])}
          onFiled={() => { setFiling([]); setSelected(new Set()); void refresh(); }}
        />
      </div>
    </Layout>
  );
}
